import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './TreasuryStrategy.css';

/* Line-drawing SVG icons */
const SpokeIcon = ({ type }) => {
  const icons = {
    portfolio: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="16" cy="16" r="12" />
        <circle cx="16" cy="16" r="6" />
        <circle cx="16" cy="16" r="1.5" fill="#1B4620" />
      </svg>
    ),
    growth: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6,26 13,17 19,21 26,10" />
        <polyline points="20,10 26,10 26,16" />
      </svg>
    ),
    geo: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="16" cy="16" r="12" />
        <ellipse cx="16" cy="16" rx="5" ry="12" />
        <line x1="4" y1="16" x2="28" y2="16" />
      </svg>
    ),
    staffing: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="16" cy="11" r="4" />
        <path d="M8 26c0-4.4 3.6-8 8-8s8 3.6 8 8" />
        <circle cx="25" cy="9" r="3" />
        <path d="M28 22c0-2.8-1.8-5-4.5-5.5" />
      </svg>
    ),
    community: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 26V14l4-4 4 4v12" />
        <line x1="12" y1="18" x2="20" y2="18" />
        <line x1="16" y1="14" x2="16" y2="18" />
        <path d="M6 26V18l3-3" />
        <path d="M26 26V18l-3-3" />
        <line x1="4" y1="26" x2="28" y2="26" />
      </svg>
    ),
    risk: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1B4620" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 4L28 27H4L16 4z" />
        <line x1="16" y1="13" x2="16" y2="20" />
        <circle cx="16" cy="23" r="0.5" fill="#1B4620" />
      </svg>
    ),
  };
  return icons[type] || null;
};

const spokes = [
  { label: 'Portfolio Composition', iconType: 'portfolio', desc: 'Current loan mix, asset quality, and concentration trends' },
  { label: 'Growth Targets', iconType: 'growth', desc: 'Origination goals, AUM growth, and deployment timelines' },
  { label: 'Geographic Trends', iconType: 'geo', desc: 'Service area dynamics, market demand, and regional opportunity' },
  { label: 'Staffing & Capacity', iconType: 'staffing', desc: 'Team size, expertise gaps, and operational bandwidth' },
  { label: 'Communities Served', iconType: 'community', desc: 'Target populations, borrower needs, and mission alignment' },
  { label: 'Risk Appetite', iconType: 'risk', desc: 'Loss tolerance, concentration limits, and reserve targets' },
];

const UnderwritingLending = () => {
  return (
    <>
      <Navbar />
      <main className="treasury-page uw-page" style={{ paddingTop: '80px' }}>

        {/* Combined Header + Lending Strategy Diagram */}
        <section className="hub-section">
          <div className="hub-container">
            <div className="treasury-page-header" style={{ marginBottom: '1.25rem', borderLeft: 'none', boxShadow: 'none', background: 'transparent', padding: '1rem 0 0' }}>
              <Link to="/" className="treasury-back">&larr; Home</Link>
              <h1>Underwriting &amp; Lending Strategy</h1>
              <p>
                Deep experience in charter school underwriting, health clinic financing, NMTC allocation, and leverage loan underwriting.
              </p>
            </div>

            <h2 className="hub-section-title">What Informs a Lending Strategy</h2>

            <div className="hub-wrapper">
              {/* Top row: first 3 spokes */}
              <div className="hub-row">
                {spokes.slice(0, 3).map((spoke, i) => (
                  <div key={i} className="spoke-item">
                    <div className="spoke-icon"><SpokeIcon type={spoke.iconType} /></div>
                    <div className="spoke-label">{spoke.label}</div>
                    <div className="spoke-desc">{spoke.desc}</div>
                  </div>
                ))}
              </div>

              {/* Arrows + Center */}
              <div className="hub-arrows">
                <span>&#9660;</span><span>&#9660;</span><span>&#9660;</span>
              </div>
              <div className="hub-center">
                <div className="hub-center-inner">
                  <span>Lending</span>
                  <span>Strategy</span>
                </div>
              </div>
              <div className="hub-arrows">
                <span>&#9650;</span><span>&#9650;</span><span>&#9650;</span>
              </div>

              {/* Bottom row: last 3 spokes */}
              <div className="hub-row">
                {spokes.slice(3, 6).map((spoke, i) => (
                  <div key={i + 3} className="spoke-item">
                    <div className="spoke-icon"><SpokeIcon type={spoke.iconType} /></div>
                    <div className="spoke-label">{spoke.label}</div>
                    <div className="spoke-desc">{spoke.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECTION 2: Underwriting ===== */}
        <section className="treasury-main-section">
          <div className="treasury-container">
            <h2 className="uw-full-title">Outsourced Underwriting Services</h2>
            <p className="uw-section-intro" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 2rem' }}>
              Acting as your <strong>outsourced underwriting partner</strong> — building the models, policies, and tools your team needs to underwrite with consistency and confidence.
            </p>

            <div className="uw-grid">
              {/* Step 1 */}
              <div className="flow-step">
                <div className="flow-icon">1</div>
                <div className="flow-card step-thesis">
                  <h3>Credit Analysis &amp; Model Design</h3>
                  <p>
                    Building custom <strong>credit scoring models</strong>, risk rating frameworks, and financial analysis templates tailored to your lending products.
                  </p>
                  <div className="flow-tags">
                    <span>Credit Scoring</span>
                    <span>Risk Rating</span>
                    <span>Financial Analysis</span>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flow-step">
                <div className="flow-icon">2</div>
                <div className="flow-card step-cdfi">
                  <h3>Policies &amp; Approval Framework</h3>
                  <p>
                    Drafting <strong>credit policies</strong>, approval authority matrices, exception frameworks, and compliance-aligned procedures.
                  </p>
                  <div className="flow-tags">
                    <span>Credit Policy</span>
                    <span>Approval Matrix</span>
                    <span>Compliance</span>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flow-step">
                <div className="flow-icon">3</div>
                <div className="flow-card step-fundraise">
                  <h3>Templates &amp; Checklists</h3>
                  <p>
                    Creating ready-to-use <strong>credit memo templates</strong>, due diligence checklists, closing document packages, and monitoring tools.
                  </p>
                  <div className="flow-tags">
                    <span>Credit Memos</span>
                    <span>Due Diligence</span>
                    <span>Closing Docs</span>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flow-step">
                <div className="flow-icon">4</div>
                <div className="flow-card step-close">
                  <h3>Training &amp; Quality Control</h3>
                  <p>
                    <strong>Staff training</strong> on new models and workflows, QC reviews of loan files, and ongoing support to build internal capacity.
                  </p>
                  <div className="flow-tags">
                    <span>Staff Training</span>
                    <span>QC Reviews</span>
                    <span>Ongoing Support</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Expertise areas */}
            <div className="uw-expertise" style={{ maxWidth: '700px', margin: '2rem auto 0' }}>
              <h4>Sector Expertise</h4>
              <div className="flow-tags">
                <span>Charter Schools</span>
                <span>Health Clinics</span>
                <span>NMTC Allocation</span>
                <span>Leverage Lending</span>
                <span>Small Business</span>
                <span>Affordable Housing</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="treasury-cta">
          <div className="treasury-container">
            <h2>Let's Build Your Lending Platform</h2>
            <p>
              Whether you need an outsourced underwriting partner, a refreshed lending strategy,
              or both — we're ready to help your team deploy capital with confidence.
            </p>
            <Link to="/" state={{ scrollTo: 'contact' }} className="treasury-cta-btn">
              Get in Touch &rarr;
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default UnderwritingLending;
